import React from 'react';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import HourglassFull from '@mui/icons-material/HourglassFull';
import Verified from '@mui/icons-material/Verified';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import { Tooltip, Box, Chip } from '@mui/material';

/* ───────────────────────── Config de organización ──────────────────────── */
export const FIELD_GROUPS = [
  { id: 'principal', title: 'Datos principales', primary: ['ID', 'FOLIO', 'PROVEEDOR', 'IMPORTE', 'CENTROCOSTOS', 'USER_NAME', 'NOMBRE'] },
  { id: 'fechas', title: 'Fechas', fields: ['FECHA_CREACION', 'FECHA_AUTORIZACION', 'FECHA_VENCIMIENTO', 'accion'] },
  { id: 'responsables', title: '', fields: ['SOLICITANTE', 'AUTORIZA', 'AREA', 'DEPARTAMENTO', 'CENTRO_COSTO'] },
  { id: 'autorizaciones', title: 'Autorizaciones', fields: ['AUTNIVEL1', 'AUTNIVEL2', 'AUTNIVEL3', 'CONFIRMARPRECIO', 'Usuario'] },
  { id: 'otros', title: 'Otros', fields: ['OC', 'OBSERVACIONES', 'MONEDA', 'TIPO_PAGO', 'PERFIL'] },
];

export function orderFields(visibleKeys) {
  const used = new Set(); const out = [];
  const primary = FIELD_GROUPS.flatMap(g => g.primary || []);
  primary.forEach(k => { if (visibleKeys.includes(k) && !used.has(k)) { used.add(k); out.push(k); } });
  FIELD_GROUPS.forEach(g => (g.fields || []).forEach(k => {
    if (visibleKeys.includes(k) && !used.has(k)) { used.add(k); out.push(k); }
  }));
  visibleKeys.forEach(k => { if (!used.has(k)) out.push(k); });
  return out;
}

export const COLUMN_LABELS = {
  ID: 'ID', FOLIO: 'Folio OC', PROVEEDOR: 'Proveedor', NUMEROCC: 'No. Centro Costo',
  CENTROCOSTOS: 'Centro de costo', CENTRO_COSTO: 'Centro de costo', DEPARTAMENTO: 'Departamento',
  AREA: 'Área', USER_NAME: 'Usuario ERP', NOMBRE: 'Usuario', FOLIO_FACTURA: 'Folio factura',
  FECHA_FACTURA: 'Fecha factura', PAGO: 'Tipo de pago', TIPO_PAGO: 'Tipo de pago',
  NUMERO_ECONOMICO: 'No. económico', IMPORTE: 'Importe OC', MONTOSOLICITADO: 'Monto solicitado',
  TOTAL: 'Total OC', ESTATUS: 'Estatus', ACTIVO: 'Activo', FECHA_CREACION: 'Fecha creación',
  FECHA_AUTORIZACION: 'Fecha autorización', FECHA_VENCIMIENTO: 'Fecha vencimiento', MONEDA: 'Moneda',
  PERFIL: 'Perfil', SOLICITANTE: 'Solicitante', AUTORIZA: 'Autoriza', CANCELADO_ERP: 'Activo ERP',
};

export const getColumnLabel = (key, labels = {}) => labels[key] || COLUMN_LABELS[key] || key;

export const formatDate = (date) => {
  if (!date) return '';
  return typeof date === 'object' ? date.date : date;
};

export const formatCellValue = (key, value) => {
  if (key === 'FOLIO_FACTURA') {
    const str = (value ?? '').toString().trim();
    if (!str || str === '0') return 'Sin folio';
    return str;
  }
  if (key === 'EMPRESA') {
    const str = (value ?? '').toString().trim().toUpperCase();
    const map = {
      'ESEASA CONSTRUCCIONES': 'CONSTRUCCIONES',
      'ESEASA OFFSHORE': 'OFFSHORE',
      'PESADO TRANSPORT': 'PESADO',
      'GIMSA': 'GIMSA',
    };
    return map[str] || value || '';
  }
  if (key === 'FECHA_FACTURA') return value ? formatDate(value) : 'Sin fecha';
  if (key === 'PAGO' || key === 'TIPO_PAGO') {
    const v = (value ?? '').toString().toUpperCase().trim();
    if (v === 'CONTADO') return 'Contado';
    if (v === 'CREDITO') return 'Crédito';
    if (!v || v === '0') return 'N/A';
    return v;
  }
  if (key === 'NUMERO_ECONOMICO' || key === 'NUMERO_ECO') return (value ?? '').toString().trim() || 'S/N';
  if (key === 'CONCEPTOPAGO' || key === 'OBSERVACIONES' || key === 'NOTAS') return (value ?? '').toString().trim() || 'S/N';
  if (key === 'CANCELADO_ERP') return Number(value) === 1 ? 'Cancelado' : (Number(value) === 0 ? 'Activo' : '');
  
  return formatDate(value);
};

export const renderAuthStatus = (value, motivoCancelacion) => {
  if (value === 'N/A') return "N/A";
  const numericValue = Number(value);
  if (numericValue === 1) return <Verified sx={{ color: '#10b981' }} />; // Vibrant green
  if (numericValue === 0) return <HourglassFull sx={{ color: '#f59e0b' }} />; // Vibrant orange
  switch (value) {
    case 'CHECK_GREEN': return <CheckCircle sx={{ color: '#10b981' }} />;
    case 'PENDING_YELLOW': return <TimerRoundedIcon sx={{ fontSize: 24, color: '#f59e0b' }} />;
    case 'CANCEL_RED':
      return (
        <Tooltip title={motivoCancelacion || 'Sin motivo'} arrow disableInteractive slotProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}><Cancel sx={{ color: '#ef4444' }} /></Box>
        </Tooltip>
      );
    default: return value;
  }
};

export const renderCanceladoERP = (value) => {
  const n = Number(value);
  if (n === 1) return <Cancel sx={{ color: '#ef4444' }} />;
  if (n === 0) return <CheckCircle sx={{ color: '#10b981' }} />;
  return '';
};

export const CANCELADO_ERP_BG = 'rgba(239, 68, 68, 0.08)'; // Premium light red

export const renderStatusColor = (status, confirmarPrecio, canceladoErp) => {
  if (Number(canceladoErp) === 1) return CANCELADO_ERP_BG;
  switch (status) {
    case 'En Proceso':
    case 'Proceso': return '#fef3c7';
    case 'Solicitud Parcial':
    case 'Solicitada Parcial': return '#e2f4ff';
    case 'Solicitada Total': return '#e9ddfc';
    case 'Pago Parcial':
    case 'Pagada Parcial': return '#ccfbf1';
    case 'Pago Total':
    case 'Pagado Total':
    case 'Pagada Total':
    case 'Pagada':
      return '#e8f5e9';
    case 'Autorizada': return '#e0edff';
    case 'Cancelada': return '#fde8e8';
    case 'Vencida': return '#ffe8d6';
    case 'Sin solicitud': return '#ffffff';
    case 'Tiene solicitud': return '#e8f5e9';
    default: return 'transparent';
  }
};

export const COLUMN_TYPES = { ACTIVO: 'boolean', CONFIRMARPRECIO: 'boolean', CANCELADO_ERP: 'boolean' };
export const getColumnType = (key) => COLUMN_TYPES[key] ?? 'text';

export const normalizeValue = (val, type) => {
  if (val == null) return null;
  switch (type) {
    case 'boolean':
      if (typeof val === 'boolean') return val ? 1 : 0;
      if (typeof val === 'number') return val ? 1 : 0;
      if (typeof val === 'string') return val === '1' || val.toLowerCase() === 'true' ? 1 : 0;
      return 0;
    case 'number': return Number(val);
    case 'date': {
      const raw = typeof val === 'object' && val?.date ? val.date : val;
      const t = new Date(raw).getTime();
      return isNaN(t) ? null : t;
    }
    default: return val?.toString?.().toLowerCase?.() ?? '';
  }
};

export const getRowKey = (row, index) => String(
  row?.ID ?? row?.AUTSOLPAGOID ?? row?.FOLIO ?? row?.FOLIO_OC ?? row?.FOLIO_FACTURA ?? (row?.__idx !== undefined && row?.__idx !== '' ? 'row-' + row.__idx : (index !== undefined ? 'row-' + index : 'row-' + Math.random().toString(36).substr(2, 9)))
);

export const ActiveStatusChip = ({ value }) => {
  const n = normalizeValue(value, 'boolean');
  const isActive = n === 1;
  return (
    <span>
      <Chip
        label={isActive ? 'Activo' : 'Inactivo'}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          bgcolor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
          color: isActive ? '#10b981' : '#6b7280',
          border: isActive ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(107,114,128,0.3)',
          cursor: 'default',
          pointerEvents: 'none',
        }}
      />
    </span>
  );
};

export const densityPadding = (density) => {
  switch (density) {
    case 'compact': return 0.75;
    case 'spacious': return 1.75;
    default: return 1.25;
  }
};
